import React, { useState, useEffect } from 'react';
import { useParams, useLocation, useNavigate } from 'react-router-dom';
import firebaseApp from '../Firecontact';
import {
  Box,
  Typography,
  Grid,
  Button,
  Chip,
  CircularProgress
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import { toast } from 'react-toastify';
import Header from '../Header';
import Footer from '../Footer';

// Reusing BeforeAfterSlider from AiTrendsGallery
const BeforeAfterSlider = ({ before, after }) => {
  const [sliderPos, setSliderPos] = useState(50);

  return (
    <Box sx={{ position: 'relative', width: '100%', overflow: 'hidden', bgcolor: '#0b1121' }}>
      <img src={after} alt="After" style={{ width: '100%', height: 'auto', display: 'block', backgroundColor: '#0b1121', objectFit: 'contain' }} />
      <Chip label="After AI" size="small" sx={{ position: 'absolute', top: 12, right: 12, fontWeight: '500', zIndex: 3, bgcolor: '#25d366', color: '#fff' }} />

      <img
        src={before}
        alt="Before"
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          objectFit: 'contain',
          backgroundColor: '#0b1121',
          clipPath: `polygon(0 0, ${sliderPos}% 0, ${sliderPos}% 100%, 0 100%)`,
          display: 'block'
        }}
      />
      <Chip label="Before" size="small" sx={{ position: 'absolute', top: 12, left: 12, fontWeight: '500', zIndex: 3, bgcolor: '#ffc107', color: '#131921' }} />

      <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: `${sliderPos}%`, width: '3px', bgcolor: '#ffffff', zIndex: 4, transform: 'translateX(-50%)', pointerEvents: 'none', boxShadow: '0 0 10px rgba(0,0,0,0.5)' }}>
        <Box sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', width: '36px', height: '36px', borderRadius: '50%', bgcolor: '#ffffff', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 6px rgba(0,0,0,0.4)', border: '2.5px solid #3a7bd5' }}>
          <span style={{ color: '#3a7bd5', fontWeight: 'bold', fontSize: '12px' }}>◀▶</span>
        </Box>
      </Box>

      <input
        type="range"
        min="0"
        max="100"
        value={sliderPos}
        onChange={(e) => setSliderPos(e.target.value)}
        style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', opacity: 0, zIndex: 5, cursor: 'ew-resize', margin: 0 }}
      />
    </Box>
  );
};

export default function PromptDetails() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const [trend, setTrend] = useState(location.state?.trend || null);
  const [loading, setLoading] = useState(!location.state?.trend);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (!trend && id) {
      const fetchTrend = async () => {
        try {
          const db = firebaseApp.firestore();
          const doc = await db.collection('ai_trends').doc(id).get();
          if (doc.exists) {
            setTrend({ id: doc.id, ...doc.data() });
          } else {
            toast.error("Prompt not found!");
            navigate('/ai-content-studio');
          }
        } catch (error) {
          console.error("Error fetching trend:", error);
          toast.error("Failed to load prompt details.");
        } finally {
          setLoading(false);
        }
      };
      fetchTrend();
    }
  }, [id, trend, navigate]);

  const handleCopyPrompt = (promptText) => {
    const plainText = (promptText || '').replace(/<[^>]*>/g, '');
    navigator.clipboard.writeText(plainText);
    setCopiedId(trend.id);
    toast.success("Prompt copied to clipboard!");
    setTimeout(() => setCopiedId(null), 2000);
  };

  if (loading) {
    return (
      <Box sx={{ minHeight: '100vh', background: 'radial-gradient(circle at 0% 30%, rgba(232, 255, 0, 0.12) 0%, transparent 45%), radial-gradient(circle at 100% 30%, rgba(0, 100, 255, 0.15) 0%, transparent 40%), #0b1121' }}>
        <Header name="AiContentStudio" />
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={50} sx={{ color: '#e8ff00' }} />
        </Box>
        <Footer name="AiContentStudio" />
      </Box>
    );
  }

  if (!trend) return null;

  return (
    <Box sx={{ minHeight: '100vh', fontFamily: '"Poppins", sans-serif', background: 'radial-gradient(circle at 0% 30%, rgba(232, 255, 0, 0.12) 0%, transparent 45%), radial-gradient(circle at 100% 30%, rgba(0, 100, 255, 0.15) 0%, transparent 40%), #0b1121' }}>
      <Header name="AiContentStudio" />
      <Box sx={{ pt: { xs: 6, md: 8 }, pb: { xs: 6, md: 8 } }}>
        <div className="container">
          <Box sx={{ maxWidth: '1000px', mx: 'auto', bgcolor: 'rgba(255, 255, 255, 0.02)', backdropFilter: 'blur(20px)', borderRadius: 4, border: '1px solid rgba(255, 255, 255, 0.08)', overflow: 'hidden', boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.5)' }}>

            <Box sx={{ p: { xs: 3, sm: 4 }, borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
              <Typography sx={{ fontWeight: '600', fontSize: { xs: '1.5rem', sm: '2rem' }, color: '#ffffff', fontFamily: '"Poppins", sans-serif', display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
                {trend.title}
                <Chip label={trend.category} size="small" sx={{ fontWeight: '600', bgcolor: 'rgba(232, 255, 0, 0.1)', color: '#e8ff00', border: '1px solid rgba(232,255,0,0.2)' }} />
              </Typography>
            </Box>

            <Box sx={{ p: { xs: 2, sm: 4 } }}>
              <Grid container spacing={{ xs: 3, md: 4 }}>
                {/* Image Section */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ position: 'relative', width: '100%', height: '100%', minHeight: '300px', borderRadius: 3, overflow: 'hidden', bgcolor: 'rgba(255,255,255,0.05)', boxShadow: 'inset 0 0 10px rgba(0,0,0,0.2)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    {trend.beforeImageUrl && trend.afterImageUrl ? (
                      <Box sx={{ width: '100%', height: '100%', '& img': { width: '100%', height: '100%', objectFit: 'contain', display: 'block' } }}>
                        <BeforeAfterSlider before={trend.beforeImageUrl} after={trend.afterImageUrl} />
                      </Box>
                    ) : (
                      <Box sx={{ width: '100%', height: '100%', position: 'relative', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <img src={trend.afterImageUrl || trend.beforeImageUrl} alt={trend.title} style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain', display: 'block', backgroundColor: '#0b1121' }} />
                      </Box>
                    )}
                  </Box>
                </Grid>

                {/* Content Section */}
                <Grid item xs={12} md={6} sx={{ display: 'flex', flexDirection: 'column' }}>
                  <Typography variant="body1" sx={{ mb: 3, color: 'rgba(255,255,255,0.7)', fontWeight: 300, fontFamily: '"Poppins", sans-serif', fontSize: '1.05rem' }}>
                    {trend.shortDescription}
                  </Typography>

                  <Typography variant="subtitle2" sx={{ mb: 1, color: '#ffffff', fontWeight: '600', fontFamily: '"Poppins", sans-serif' }}>
                    Optimized AI Prompt:
                  </Typography>
                  <Box
                    sx={{
                      p: { xs: 2, sm: 3 },
                      bgcolor: 'rgba(0,0,0,0.3)',
                      borderRadius: 3,
                      fontFamily: 'monospace',
                      fontSize: '0.9rem',
                      color: '#e8ff00',
                      whiteSpace: 'pre-wrap',
                      border: '1px solid rgba(255,255,255,0.1)',
                      flexGrow: 1,
                      overflowY: 'auto',
                      mb: 3,
                      boxShadow: 'inset 0 2px 10px rgba(0,0,0,0.2)'
                    }}
                    dangerouslySetInnerHTML={{ __html: trend.fullPrompt }}
                  />

                  <Box sx={{ display: 'flex', gap: 2 }}>
                    <Button
                      fullWidth
                      startIcon={copiedId === trend.id ? <CheckIcon /> : <ContentCopyIcon />}
                      onClick={() => handleCopyPrompt(trend.fullPrompt)}
                      sx={{
                        borderRadius: 2,
                        fontWeight: '700',
                        textTransform: 'uppercase',
                        fontFamily: '"Poppins", sans-serif',
                        py: 1.5,
                        bgcolor: copiedId === trend.id ? '#25d366' : '#e8ff00',
                        color: copiedId === trend.id ? 'white' : '#0b1121',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          bgcolor: copiedId === trend.id ? '#21b858' : '#d4e600',
                          transform: 'scale(1.02)'
                        }
                      }}
                    >
                      {copiedId === trend.id ? "Copied!" : "Copy Full Prompt"}
                    </Button>
                    <Button onClick={() => navigate(-1)} variant="outlined" sx={{
                      borderRadius: 2,
                      fontWeight: '600',
                      textTransform: 'uppercase',
                      color: 'rgba(255,255,255,0.6)',
                      borderColor: 'rgba(255,255,255,0.2)',
                      fontFamily: '"Poppins", sans-serif',
                      '&:hover': { borderColor: '#e8ff00', bgcolor: 'rgba(232, 255, 0, 0.05)', color: '#e8ff00' }
                    }}>
                      Back
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Box>
          </Box>
        </div>
      </Box>
      <Footer name="AiContentStudio" />
    </Box>
  );
}
